/**
 * `AW10` · Borde HTTP de la pantalla «Envíos» de `F2`.
 *
 * La pantalla del Management Tool habla SOLO con `apps/admin-web` por HTTP y no
 * guarda estado (`arquitectura.html` §1: «no hay proceso que operar: es un
 * bucket»). Este archivo es el único de `envios/` que emite `fetch` (guard ①):
 * lista, detalle, destinatarios, creación y cancelación, con la forma que
 * `contracts/dispatch-api/http.ts` declara.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ① LO QUE ENTRA DEL REPO SIGUEN SIENDO SÓLO TIPOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Misma regla que `borde-escritura.ts` y `borde-participantes.ts`: la pantalla no
 * importa `modules/**` y los imports de `contracts/**` son `import type`. El alias
 * `@contracts/*` vive en `tsconfig.json` y NO en `vite.config.ts`, así que acá los
 * imports van por RUTA RELATIVA (`../../../../contracts/…`): el alias sólo lo
 * resuelve `tsc`, y un `import` que el bundler no sepa resolver es un build roto
 * que el typecheck no ve. Por eso las rutas se repiten acá
 * como literales, **tipados contra los tipos de path del contrato**: el día que una
 * ruta cambie en `contracts/dispatch-api/http.ts`, este build se cae en vez de
 * seguir hablándole a un endpoint que no existe.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ② EL BRAZO Y EL ACTOR NO CRUZAN POR EL NAVEGADOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CreateDispatchCommand` lleva `arm` y `actor`, pero el cuerpo del cable los
 * prohíbe expresamente (`arm?: never`, `actor?: never`). Acá se envía un
 * `Omit<CreateDispatchCommand, "arm">` y `admin-web` resuelve el brazo y el actor
 * desde la sesión autenticada (`ADR-0006`). Una auditoría que el navegador pudiera
 * escribir sería una auditoría que el auditado escribe.
 */

import type {
  CreateDispatchRequestBody,
  DispatchCancelPath,
  DispatchPath,
  DispatchRecipientsPath,
  DispatchesPath,
} from "../../../../contracts/dispatch-api/http.ts"
import type {
  DispatchDetail,
  DispatchListPage,
  DispatchListQuery,
  DispatchReasonBreakdown,
  DispatchRow,
  ScheduledForLocal,
} from "../../../../contracts/dispatch-api/index.ts"
import type {
  CancelOutcome,
  DispatchFailure,
  DispatchFailureKind,
  DispatchId,
  DispatchRecipient,
  DispatchRecord,
  DispatchState,
  RecipientState,
} from "../../../../contracts/dispatch/index.ts"
import type { TemplateParameterSource } from "../../../../contracts/channel/index.ts"
import type { ConfiguracionInstancia } from "../configuracion.ts"
import { mensajeDeError } from "../mensaje-de-error.ts"
import { raizDelBrazo } from "../brazo-del-cable.ts"

/**
 * El borde de envíos visto desde el navegador.
 *
 * Sigue la forma del contrato pero adapta las entradas que la pantalla puede
 * emitir: el `actor` y el `arm` no entran por acá.
 */
export type BordeEnvios = {
  readonly listar: (query: DispatchListQuery) => Promise<DispatchListPage>
  /**
   * ⭐ `R2-UI3` · **devuelve `DispatchDetail` y no `DispatchRow`**: el detalle
   * lleva los dos desgloses por motivo, que la lista no trae (`R2-CDA1`, `ADR-0328`).
   */
  readonly detalle: (id: DispatchId) => Promise<DispatchDetail | null>
  readonly destinatarios: (id: DispatchId) => Promise<readonly DispatchRecipient[]>
  readonly crear: (command: ComandoDeEnvio) => Promise<DispatchRecord>
  readonly cancelar: (id: DispatchId) => Promise<CancelOutcome>
}

/**
 * `R2-UI2` · **la selección total viaja como CRITERIO, no como ids.**
 *
 * `B26`/`RT 6` y `ADR-0328`: cuando el operador eligió «todos los elegibles del
 * filtro», el cuerpo lleva `audience: {lotes, estados, exclude}` —el filtro aplicado
 * y los destildados— y el servidor resuelve el resto en la MISMA transacción que
 * crea el envío. Materializar la lista de ids en el navegador es lo que `ADR-0090`
 * descartó: serían las ~16.000 filas del padrón cruzando al cliente.
 *
 * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI15`** (R2-H-045). Este tipo era
 * ~~`Omit<CreateDispatchCommand, "arm"> & { readonly exclude?: readonly RecipientRef[] }`~~:
 * un contrato paralelo que compilaba solo y que el servidor no reconoce (`exclude`
 * suelto en la raíz ⇒ `400`, CA-49). Ahora sale de `CreateDispatchRequestBody`
 * (`contracts/dispatch-api/http.ts`): O `audience` O `recipients`, nunca las dos,
 * y una clave que el contrato no declara no compila. Lo único que la consola cambia
 * es la fecha: entra como `scheduledFor: Date | null` y `crear` la escribe como
 * `scheduledForLocal`. `arm` y `actor` no entran por acá.
 */
type SinClaves<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never
export type ComandoDeEnvio = SinClaves<
  CreateDispatchRequestBody,
  "scheduledForLocal" | "arm" | "actor"
> & {
  readonly scheduledFor: Date | null
}

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

/**
 * El acuerdo de cable de los envíos, todo junto y en un solo lugar.
 *
 * Los valores literales repiten los del contrato; los tipos de path son del
 * contrato, así que una divergencia rompe el typecheck y no pasa desapercibida.
 */
const RUTA_ENVIOS: DispatchesPath = "envios"
const SEGMENTO_DESTINATARIOS = "destinatarios"
const SEGMENTO_CANCELACION = "cancelacion"

/**
 * ⭐ `R2-UI2` · El sub-recurso que arma la lista «Plantillas para envíos» del
 * brazo (`B13`/`RT 9`). Es la fuente del selector de Nuevo envío: lo que se
 * OFRECE es lo que el brazo puso en la lista, no el catálogo entero. El valor
 * repite el de `contracts/admin-api/http.ts` (`SEND_LIST_SEGMENT`).
 */
export const SEGMENTO_LISTA_PARA_ENVIOS = "plantillas-para-envios"

function rutaEnvio(id: DispatchId): DispatchPath {
  return `${RUTA_ENVIOS}/${encodeURIComponent(id)}`
}

function rutaDestinatarios(id: DispatchId): DispatchRecipientsPath {
  return `${RUTA_ENVIOS}/${encodeURIComponent(id)}/${SEGMENTO_DESTINATARIOS}`
}

function rutaCancelacion(id: DispatchId): DispatchCancelPath {
  return `${RUTA_ENVIOS}/${encodeURIComponent(id)}/${SEGMENTO_CANCELACION}`
}

/**
 * Nombres de los query params de paginación.
 *
 * El contrato exporta los valores como constantes (`DISPATCH_PAGE_PARAM`,
 * `DISPATCH_PAGE_SIZE_PARAM`), pero no exporta un tipo de path/parámetro para
 * anclarlos: no hay otra forma de escribirlos que a mano.
 */
const PARAMETRO_PAGINA = "pagina"
const PARAMETRO_TAMANO = "tamano"

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null && !Array.isArray(valor)
}

/**
 * Comprueba en runtime lo que el tipo promete y `response.json()` no puede saber.
 *
 * `DispatchRecord` trae `scheduledFor: Date | null`; por el cable llega como
 * string ISO 8601 o `null`, así que acá se revive con `new Date(...)`.
 */
function leerRegistro(valor: unknown): DispatchRecord | null {
  if (!esObjeto(valor)) return null

  const id = valor["id"]
  if (typeof id !== "string") return null

  const arm = valor["arm"]
  if (typeof arm !== "string") return null

  const name = valor["name"]
  if (typeof name !== "string") return null

  /**
   * `R2-UI3` · `TemplateContent` pide `language` y `parameters`, y los dos son
   * DATO, no adorno: `language` porque Meta identifica la plantilla por el par
   * `(name, language)`; `parameters` porque es el orden de las variables. Se leen
   * del cable y se validan uno por uno — nada de `as TemplateContent`.
   */
  const templateCrudo = valor["template"]
  if (!esObjeto(templateCrudo)) return null
  const kind = templateCrudo["kind"]
  if (kind !== "plantilla") return null
  const templateName = templateCrudo["name"]
  if (typeof templateName !== "string") return null
  const templateLanguage = templateCrudo["language"]
  if (typeof templateLanguage !== "string") return null
  const parameters = leerParametros(templateCrudo["parameters"])
  if (parameters === null) return null

  const audienceLabel = valor["audienceLabel"]
  if (typeof audienceLabel !== "string") return null

  /**
   * ⭐ `R2-UI2` · `B29`/`CA-50`: **sin filtro la segunda línea de Audiencia NO
   * existe**, y el cable la manda como `null` (el contrato la tipa `string`; el
   * `null` es la ausencia en runtime, `contracts/dispatch/index.ts:370`).
   *
   * ⛔ No se descarta la fila por eso: descartarla borraría de la lista ENTERA los
   * envíos creados sin filtro —los de CA-50—, que es peor que mostrar una línea
   * donde van dos. Se normaliza la ausencia a `""` y el render decide con eso.
   */
  const audienceFilterLabelCrudo = valor["audienceFilterLabel"]
  if (
    audienceFilterLabelCrudo !== null &&
    audienceFilterLabelCrudo !== undefined &&
    typeof audienceFilterLabelCrudo !== "string"
  ) {
    return null
  }
  const audienceFilterLabel =
    typeof audienceFilterLabelCrudo === "string" ? audienceFilterLabelCrudo : ""

  const audienceSize = valor["audienceSize"]
  if (typeof audienceSize !== "number" || !Number.isInteger(audienceSize) || audienceSize < 0) {
    return null
  }

  const scheduledForCrudo = valor["scheduledFor"]
  if (scheduledForCrudo !== null && typeof scheduledForCrudo !== "string") return null

  const createdAtCrudo = valor["createdAt"]
  if (typeof createdAtCrudo !== "string") return null
  const createdAt = new Date(createdAtCrudo)
  if (Number.isNaN(createdAt.getTime())) return null

  const state = valor["state"]
  if (typeof state !== "string") return null

  const failureCrudo = valor["failure"]
  let failure: DispatchFailure | null = null
  if (failureCrudo !== null) {
    if (!esObjeto(failureCrudo)) return null
    /**
     * `R2-CDS1`/`ADR-0327` · `kind` y `errorText` son OBLIGATORIOS y `errorCode`
     * opcional: tres de las cuatro clases no tienen código que leer. Antes se
     * armaba `{ errorCode }` y eso ya no es un `DispatchFailure`.
     */
    const failureKind = failureCrudo["kind"]
    if (!esFailureKind(failureKind)) return null
    const errorText = failureCrudo["errorText"]
    if (typeof errorText !== "string") return null
    const errorCode = failureCrudo["errorCode"]
    if (errorCode !== undefined && (typeof errorCode !== "number" || !Number.isInteger(errorCode))) {
      return null
    }
    failure = errorCode === undefined ? { kind: failureKind, errorText } : { kind: failureKind, errorText, errorCode }
  }

  return {
    id,
    arm,
    name,
    template: { kind, name: templateName, language: templateLanguage, parameters },
    /**
     * `DispatchRecord.templateVariables` es el ORDEN de `template.parameters`
     * (`R2-CDS1`): no es una copia de valores. Se toma de la MISMA fuente que
     * `template.parameters` para que no puedan discrepar.
     */
    templateVariables: parameters,
    audienceLabel,
    audienceFilterLabel,
    audienceSize,
    scheduledFor: scheduledForCrudo === null ? null : new Date(scheduledForCrudo),
    createdAt,
    state: state as DispatchState,
    failure,
  }
}

/** Valida el vocabulario CERRADO de `TemplateParameterSource` (`ADR-0058`). */
function leerParametros(valor: unknown): readonly TemplateParameterSource[] | null {
  if (!Array.isArray(valor)) return null
  const parametros: TemplateParameterSource[] = []
  for (const item of valor) {
    if (item !== "nombre" && item !== "telefono") return null
    parametros.push(item)
  }
  return parametros
}

/** Valida las cuatro clases de `DispatchFailureKind`. */
function esFailureKind(valor: unknown): valor is DispatchFailureKind {
  return (
    valor === "meta" ||
    valor === "plantilla" ||
    valor === "todos-excluidos" ||
    valor === "atraso"
  )
}

/** Valida una fila del listado: el envío más los tres conteos calculados al leer. */
function leerFila(valor: unknown): DispatchRow | null {
  if (!esObjeto(valor)) return null

  const dispatch = leerRegistro(valor["dispatch"])
  if (dispatch === null) return null

  const countsCrudo = valor["counts"]
  if (!esObjeto(countsCrudo)) return null

  const enviados = countsCrudo["enviados"]
  const entregados = countsCrudo["entregados"]
  const leidos = countsCrudo["leidos"]
  const fallidos = countsCrudo["fallidos"]

  if (
    typeof enviados !== "number" ||
    !Number.isInteger(enviados) ||
    enviados < 0 ||
    typeof entregados !== "number" ||
    !Number.isInteger(entregados) ||
    entregados < 0 ||
    typeof leidos !== "number" ||
    !Number.isInteger(leidos) ||
    leidos < 0 ||
    typeof fallidos !== "number" ||
    !Number.isInteger(fallidos) ||
    fallidos < 0
  ) {
    return null
  }

  return {
    dispatch,
    counts: { enviados, entregados, leidos, fallidos },
  }
}

/** Valida una página del listado: items + total en la base. */
function leerPagina(valor: unknown): DispatchListPage | null {
  if (!esObjeto(valor)) return null

  const itemsCrudo = valor["items"]
  if (!Array.isArray(itemsCrudo)) return null

  const items: DispatchRow[] = []
  for (const item of itemsCrudo) {
    const fila = leerFila(item)
    if (fila === null) return null
    items.push(fila)
  }

  const total = valor["total"]
  if (typeof total !== "number" || !Number.isInteger(total) || total < 0) return null

  return { items, total }
}

/**
 * ⭐ `R2-UI3` · Valida UN desglose por motivo (`DispatchReasonBreakdown`).
 *
 * ⛔ **Y respeta el ORDEN en que llega**, no lo rearma: el contrato fija que el
 * desglose viaja de mayor a menor y que quien lo muestra no reordena
 * (`ADR-0328`). Ordenar acá —o en la pantalla— sería una segunda regla que puede
 * discrepar de la del servidor sin que nada se ponga rojo.
 */
function leerDesglose(valor: unknown): readonly DispatchReasonBreakdown[] | null {
  if (!Array.isArray(valor)) return null

  const desglose: DispatchReasonBreakdown[] = []
  for (const item of valor) {
    if (!esObjeto(item)) return null

    /**
     * ⚠️ `reason` es el motivo **en palabras**, ya traducido por el servidor: el
     * borde no lo interpreta ni lo mapea a partir de un código (`RT 8`).
     */
    const reason = item["reason"]
    if (typeof reason !== "string") return null

    const count = item["count"]
    if (typeof count !== "number" || !Number.isInteger(count) || count < 0) return null

    desglose.push({ reason, count })
  }

  return desglose
}

/**
 * ⭐ `R2-UI3` · Valida el detalle: la MISMA fila del listado MÁS los dos desgloses.
 *
 * Extiende `leerFila` en vez de repetirla —`DispatchDetail extends DispatchRow`—,
 * así que el día que la fila gane un campo el detalle lo gana con ella.
 */
function leerDetalle(valor: unknown): DispatchDetail | null {
  if (!esObjeto(valor)) return null

  const fila = leerFila(valor)
  if (fila === null) return null

  const failuresByReason = leerDesglose(valor["failuresByReason"])
  if (failuresByReason === null) return null

  const exclusionsByReason = leerDesglose(valor["exclusionsByReason"])
  if (exclusionsByReason === null) return null

  return { ...fila, failuresByReason, exclusionsByReason }
}

/** Valida el sub-recurso de destinatarios de un envío. */
function leerDestinatarios(valor: unknown): readonly DispatchRecipient[] | null {
  if (!Array.isArray(valor)) return null

  const destinatarios: DispatchRecipient[] = []
  for (const item of valor) {
    if (!esObjeto(item)) return null

    const participantId = item["participantId"]
    if (typeof participantId !== "string") return null

    const state = item["state"]
    if (typeof state !== "string") return null

    const errorCode = item["errorCode"]
    if (errorCode !== null && (typeof errorCode !== "number" || !Number.isInteger(errorCode))) {
      return null
    }

    /**
     * `R2-CDS1` · `seq` (el orden de creación, desempate del código dominante),
     * `errorText` (el motivo EN PALABRAS) y `exclusionReason` (por qué quedó
     * excluido) son campos del destinatario y no se pueden omitir.
     */
    const seq = item["seq"]
    if (typeof seq !== "number" || !Number.isInteger(seq)) return null

    const errorText = item["errorText"]
    if (errorText !== null && typeof errorText !== "string") return null

    const exclusionReason = item["exclusionReason"]
    if (exclusionReason !== null && typeof exclusionReason !== "string") return null

    destinatarios.push({
      participantId,
      state: state as RecipientState,
      seq,
      errorCode,
      errorText,
      exclusionReason,
    })
  }

  return destinatarios
}

/**
 * Valida la unión discriminada que devuelve la cancelación, **sin colapsarla**.
 *
 * `cancelled: true` → devuelve `{ cancelled: true, arm }`.
 * `cancelled: false` → devuelve `{ cancelled: false, arm, state }`.
 */
function leerCancelOutcome(valor: unknown): CancelOutcome | null {
  if (!esObjeto(valor)) return null

  const cancelled = valor["cancelled"]
  if (typeof cancelled !== "boolean") return null

  const arm = valor["arm"]
  if (typeof arm !== "string") return null

  if (cancelled) {
    return { cancelled: true, arm }
  }

  const state = valor["state"]
  if (typeof state !== "string") return null

  return { cancelled: false, arm, state: state as DispatchState }
}

/**
 * ⭐ `R2-UI2` · **La programación cruza como TEXTO `dd-mm-aaaa hh:mm`, hora
 * Chile** — `scheduledForLocal` del contrato. Dejar el ISO de `scheduledFor` en
 * el cuerpo mandaría el instante del reloj del navegador, y el envío sale por un
 * brazo con hora fija.
 *
 * ⚠️ El offset `-04:00` es el de Chile en horario de invierno; el formato no lleva
 * zona porque el contrato fija la hora del brazo.
 */
function formatearProgramacionLocal(fecha: Date): ScheduledForLocal {
  const enChile = new Date(fecha.getTime() - 4 * 60 * 60 * 1000)
  const dd = String(enChile.getUTCDate()).padStart(2, "0")
  const mm = String(enChile.getUTCMonth() + 1).padStart(2, "0")
  const aaaa = String(enChile.getUTCFullYear())
  const hh = String(enChile.getUTCHours()).padStart(2, "0")
  const mi = String(enChile.getUTCMinutes()).padStart(2, "0")
  return `${dd}-${mm}-${aaaa} ${hh}:${mi}` as ScheduledForLocal
}

/**
 * Arma el borde de envíos contra la `admin-web` configurada.
 *
 * Función y no módulo con estado, por lo mismo que `crearBordeDeEscrituraHttp` y
 * `crearBordeParticipantesHttp`: la dirección llega en tiempo de ejecución
 * (`configuracion.json`) y no hay ningún momento de import en el que conocerla.
 */
export function crearBordeEnviosHttp(config: ConfiguracionInstancia): BordeEnvios {
  /**
   * ⭐ `AR5-m` · ⟦el-brazo-entra-por-ruta⟧ · **la raíz lleva el brazo adelante**, y
   * es una FUNCIÓN y no una constante porque el brazo sale de la dirección, que
   * cambia sin que este borde se vuelva a construir (`App.tsx` lo memoiza por
   * `config`). Ver `brazo-del-cable.ts`, que tiene la decisión del dueño citada.
   */
  const raiz = (): string => raizDelBrazo(config.adminWebUrl)

  /**
   * `credentials: "include"` en los cinco pedidos, y ningún `Authorization` armado
   * acá. La sesión la maneja `admin-web` (Better Auth + MFA, `ADR-0006`) en una
   * cookie `HttpOnly` que el navegador envía solo: un token que el JavaScript
   * tuviera que leer para ponerlo en un header es un token que la pantalla tuvo
   * que GUARDAR, y esta unidad no guarda nada.
   */
  const pedir = async (ruta: string, init: RequestInit): Promise<Response> => {
    let respuesta: Response
    try {
      respuesta = await fetch(`${raiz()}/${ruta}`, { ...init, credentials: "include" })
    } catch (causa) {
      throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${config.adminWebUrl}).`, 0), {
        cause: causa,
      })
    }
    if (!respuesta.ok) {
      throw errorDelBorde(
        await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
        respuesta.status,
      )
    }
    return respuesta
  }

  return {
    async listar(query: DispatchListQuery): Promise<DispatchListPage> {
      const parametros = new URLSearchParams()
      parametros.set(PARAMETRO_PAGINA, String(query.page))
      parametros.set(PARAMETRO_TAMANO, String(query.pageSize))

      const respuesta = await pedir(`${RUTA_ENVIOS}?${parametros.toString()}`, {
        method: "GET",
        headers: { Accept: "application/json" },
      })

      const pagina = leerPagina(await respuesta.json())
      if (pagina === null) {
        throw errorDelBorde(
          "El servidor contestó el listado de envíos en una forma que no se entiende.",
          502,
        )
      }
      return pagina
    },

    /**
     * `GET envios/{id}`.
     *
     * 404 significa «no existe», y en ese caso devuelve `null`. Cualquier otro
     * `!ok` es un error de red o de sesión y se lanza con el statusCode real.
     */
    async detalle(id: DispatchId): Promise<DispatchDetail | null> {
      const ruta: DispatchPath = rutaEnvio(id)

      let respuesta: Response
      try {
        respuesta = await fetch(`${raiz()}/${ruta}`, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "include",
        })
      } catch (causa) {
        throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${config.adminWebUrl}).`, 0), {
          cause: causa,
        })
      }

      if (respuesta.status === 404) return null
      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
          respuesta.status,
        )
      }

      const fila = leerDetalle(await respuesta.json())
      if (fila === null) {
        throw errorDelBorde(
          "El servidor contestó el detalle de envío en una forma que no se entiende.",
          502,
        )
      }
      return fila
    },

    async destinatarios(id: DispatchId): Promise<readonly DispatchRecipient[]> {
      const ruta: DispatchRecipientsPath = rutaDestinatarios(id)

      const respuesta = await pedir(ruta, {
        method: "GET",
        headers: { Accept: "application/json" },
      })

      const lista = leerDestinatarios(await respuesta.json())
      if (lista === null) {
        throw errorDelBorde(
          "El servidor contestó los destinatarios en una forma que no se entiende.",
          502,
        )
      }
      return lista
    },

    /**
     * `POST envios`.
     *
     * El comando entra con `scheduledFor: Date | null`; se serializa a
     * `scheduledForLocal` —texto `dd-mm-aaaa hh:mm`, hora Chile— antes de cruzar.
     * El `arm` y el `actor` no viajan: `admin-web` los resuelve de la sesión.
     */
    async crear(command: ComandoDeEnvio): Promise<DispatchRecord> {
      const { scheduledFor, ...resto } = command
      /*
       * ⭐ `R2-UI15` · el cuerpo se TIPA con el del contrato: `audience` o
       * `recipients` pasan tal cual (ninguna clave de más, ninguna agregada acá).
       */
      const cuerpo: CreateDispatchRequestBody = {
        ...resto,
        scheduledForLocal:
          scheduledFor === null ? null : formatearProgramacionLocal(scheduledFor),
      }

      const respuesta = await pedir(RUTA_ENVIOS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(cuerpo),
      })

      const registro = leerRegistro(await respuesta.json())
      if (registro === null) {
        throw errorDelBorde(
          "El servidor contestó la creación de envío en una forma que no se entiende.",
          502,
        )
      }
      return registro
    },

    /**
     * `POST envios/{id}/cancelacion`. Sin cuerpo; la respuesta es la unión
     * discriminada.
     *
     * ⛔⛔ `R2-UI3` · `guard` 4 + ⟦cancel-resuelve⟧ — **el `409` se lee, no se
     * traduce a «no existe», y su cuerpo NO se consume acá.**
     *
     * `http.ts:223` fija `cancelRejected: 409`, y el cuerpo de ese `409` es un
     * `CancelOutcome` que dice CUÁL estado impidió cancelar («ya estaba
     * `enviando`»). El mensaje se arma en la PANTALLA, así que este borde lanza el
     * error con la `Response` **sin leer**: si acá se llamara a `mensajeDeError`
     * —como hace `pedir`— el cuerpo quedaría consumido, la pantalla no podría
     * hacer `.json()` y el estado se perdería. Por eso esta rama no usa `pedir`.
     */
    async cancelar(id: DispatchId): Promise<CancelOutcome> {
      const ruta: DispatchCancelPath = rutaCancelacion(id)

      let respuesta: Response
      try {
        respuesta = await fetch(`${raiz()}/${ruta}`, {
          method: "POST",
          headers: { Accept: "application/json" },
          credentials: "include",
        })
      } catch (causa) {
        throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${config.adminWebUrl}).`, 0), {
          cause: causa,
        })
      }

      if (respuesta.status === 409) {
        throw Object.assign(errorDelBorde("No se pudo cancelar el envío.", 409), { respuesta })
      }
      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
          respuesta.status,
        )
      }

      const resultado = leerCancelOutcome(await respuesta.json())
      if (resultado === null) {
        throw errorDelBorde(
          "El servidor contestó la cancelación en una forma que no se entiende.",
          502,
        )
      }
      return resultado
    },
  }
}
